/**
 * AI Document Assistant Service
 * 
 * Service layer cho Admin quản lý Collections và Documents với File Search API
 */

import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Tạo axios instance
const axiosClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
axiosClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('quanly_truonghoc_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ==================== TYPES ====================

export interface AICollection {
  CollectionID: number;
  Name: string;
  Description: string | null;
  FileSearchStoreName: string;
  Icon: string | null;
  Color: string | null;
  DocumentCount: number;
  CreatedAt: string;
  UpdatedAt: string;
}

export interface AIDocument {
  DocumentID: number;
  Title: string;
  FileName: string;
  FilePath: string;
  MimeType: string;
  FileSize: number;
  GoogleFileId: string | null;
  FileSearchStoreName: string | null;
  FileSearchUploadedAt: string | null;
  Status: 'pending' | 'uploading' | 'active' | 'error';
  ErrorMessage: string | null;
  CollectionID: number;
  CollectionName: string;
  UploadedBy: number;
  UploadedAt: string;
}

export interface AIStatistics {
  totalCollections: number;
  totalDocuments: number;
  totalQueries: number;
  avgResponseTime: number;
  successRate: number;
  topQueries: Array<{
    query: string;
    count: number;
  }>;
  recentActivity: Array<{
    timestamp: string;
    type: string;
    description: string;
  }>;
}

export interface CreateCollectionRequest {
  name: string;
  description?: string;
  icon?: string;
  color?: string;
}

export interface UpdateCollectionRequest {
  name?: string;
  description?: string;
  icon?: string;
  color?: string;
}

// ==================== COLLECTION MANAGEMENT ====================

export const listCollections = async (): Promise<AICollection[]> => {
  const response = await axiosClient.get('/ai-assistant/admin/collections');
  return response.data.data;
};

export const createCollection = async (data: CreateCollectionRequest): Promise<AICollection> => {
  const response = await axiosClient.post('/ai-assistant/admin/collections', data);
  return response.data.data;
};

export const updateCollection = async (
  collectionId: number,
  data: UpdateCollectionRequest
): Promise<AICollection> => {
  const response = await axiosClient.put(`/ai-assistant/admin/collections/${collectionId}`, data);
  return response.data.data;
};

export const deleteCollection = async (collectionId: number): Promise<void> => {
  await axiosClient.delete(`/ai-assistant/admin/collections/${collectionId}`);
};

// ==================== DOCUMENT MANAGEMENT ====================

export const uploadDocument = async (
  file: File,
  collectionId: number,
  title?: string
): Promise<AIDocument> => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('collectionId', collectionId.toString());
  if (title) {
    formData.append('title', title);
  }

  const response = await axiosClient.post('/ai-assistant/admin/documents/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data.data;
};

export const listDocuments = async (collectionId?: number): Promise<AIDocument[]> => {
  const params = collectionId ? { collectionId } : {};
  const response = await axiosClient.get('/ai-assistant/admin/documents', { params });
  return response.data.data;
};

export const syncDocumentToFileSearch = async (documentId: number): Promise<void> => {
  await axiosClient.post(`/ai-assistant/admin/documents/${documentId}/sync`);
};

export const updateDocument = async (
  documentId: number,
  data: { title?: string; collectionId?: number }
): Promise<AIDocument> => {
  const response = await axiosClient.put(`/ai-assistant/admin/documents/${documentId}`, data);
  return response.data.data;
};

export const deleteDocument = async (documentId: number): Promise<void> => {
  await axiosClient.delete(`/ai-assistant/admin/documents/${documentId}`);
};

export const resyncCollection = async (collectionId: number, force: boolean = false): Promise<any> => {
  const params = force ? { params: { force: 'true' } } : {};
  const response = await axiosClient.post(`/ai-assistant/admin/collections/${collectionId}/resync`, {}, params);
  return response.data;
};

// ==================== ANALYTICS ====================

export const getStatistics = async (): Promise<AIStatistics> => {
  const response = await axiosClient.get('/ai-assistant/admin/analytics/statistics');
  return response.data.data;
};
